import { ParsedData, StepProps } from '@betterment/js-runtime';
import { Text } from '@betterment/samba-react';
import { Button, FormikTextInput, Heading } from '@betterment/style-closet-react';
import { Form, Formik, FormikHelpers } from 'formik';
import _partial from 'lodash/partial';

import { PinResetParams } from '../Checking/data';
import { InputGroupWithLabelAndError, applyApiErrorsToFormik } from '../Checking/form-helpers';
import { CloseButton, ModalContent, ModalOverlay } from '../Checking/modal-helpers';
import { validateConfirmPin, validateNewPin } from '../Checking/pin-validations';
import FlashErrorMessage from '../Shared/components/FlashErrorMessage';
import WorkflowLayout from '../Shared/components/WorkflowLayout';
import { locationAssign } from '../Shared/path-helpers';

const initialValues = {
  newPin: '',
  confirmPin: '',
};

export interface Props extends StepProps {
  onPinReset: (values: PinResetParams) => Promise<ParsedData<{}>>;
  goToNextStep: () => void;
}

const PinChangeModal: React.FC<React.PropsWithChildren<Props>> = ({ onPinReset, goToNextStep, flowState }) => {
  const onSubmit = async (values: PinResetParams, formik: FormikHelpers<typeof initialValues>) => {
    try {
      const response: ParsedData<{}> = await onPinReset(values);

      if (response && response.errors) {
        applyApiErrorsToFormik(formik, response.errors);
      } else {
        goToNextStep();
      }
    } finally {
      formik.setSubmitting(false);
    }
  };
  const { returnToPath: returnTo } = flowState;

  const onClose = () => {
    locationAssign(returnTo);
  };

  return (
    <ModalOverlay>
      <ModalContent aria-label="change pin">
        <CloseButton closeHandler={onClose} />
        <WorkflowLayout title="Change PIN" progressPercent={90}>
          <WorkflowLayout.Slot>
            <Heading as="h1" fontSize={[5, 6]} mb="6">
              Change your PIN
            </Heading>
            <Text
              as="p"
              css={{
                lineHeight: 'lineHeight.100',
                marginBottom: 'space.400',
              }}
            >
              It may take up to an hour after setting your PIN for it to take effect. If you ever forget your PIN,
              you’ll be able to reset it by logging into your account.
            </Text>
          </WorkflowLayout.Slot>
          <WorkflowLayout.Slot>
            <Formik<typeof initialValues>
              onSubmit={onSubmit}
              initialValues={initialValues}
              validateOnChange={false}
              validateOnBlur={false}
            >
              {({ isSubmitting, values, validateField, setTouched, touched }) => {
                return (
                  <Form>
                    <FlashErrorMessage name="base" />
                    <InputGroupWithLabelAndError fieldName="newPin" labelText="Enter new PIN">
                      {(props: { id: string; name: string; mb: string }) => (
                        <FormikTextInput
                          {...props}
                          disabled={isSubmitting}
                          type="password"
                          maxLength={4}
                          validate={validateNewPin}
                          onBlur={() => {
                            void setTouched({ ...touched, newPin: true });
                            void validateField('newPin');
                          }}
                        />
                      )}
                    </InputGroupWithLabelAndError>
                    <InputGroupWithLabelAndError fieldName="confirmPin" labelText="Confirm new PIN">
                      {(props: { id: string; name: string; mb: string }) => (
                        <FormikTextInput
                          {...props}
                          disabled={isSubmitting}
                          type="password"
                          maxLength={4}
                          validate={_partial(validateConfirmPin, values.newPin)}
                          onBlur={() => {
                            void setTouched({ ...touched, confirmPin: true });
                            void validateField('confirmPin');
                          }}
                        />
                      )}
                    </InputGroupWithLabelAndError>
                    <Button type="submit" px="10" disabled={isSubmitting}>
                      Update PIN
                    </Button>
                  </Form>
                );
              }}
            </Formik>
          </WorkflowLayout.Slot>
        </WorkflowLayout>
      </ModalContent>
    </ModalOverlay>
  );
};

export default PinChangeModal;
